import { useNavigate, useLocation } from "react-router-dom";
import { Users, Package, FileText, UserCog, Settings, LogOut, Moon, Sun, ChevronRight, MapPinned, ListTodo, UserRound } from "lucide-react";
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/lib/theme";
import { initials } from "@/lib/format";
import { useTaskSummary } from "@/lib/tasks";
import { cn } from "@/lib/utils";

const LINKS = [
  { to: "/huskeliste", label: "Huskeliste", desc: "Små og store jobbar som skal hugsast", icon: ListTodo, testid: "more-nav-huskeliste", badge: "tasks" },
  { to: "/kart", label: "Kart", desc: "Maskiner og fristar på kart, planlegg tur", icon: MapPinned, testid: "more-nav-kart", feature: "map_enabled" },
  { to: "/kunder", label: "Kundar", desc: "Firma og kontaktpersonar", icon: Users, testid: "more-nav-kunder" },
  { to: "/rapportar", label: "Rapportar", desc: "Service- og jobbrapportar, PDF", icon: FileText, testid: "more-nav-rapportar" },
  { to: "/deler", label: "Oljer & filter", desc: "Delar knytte til maskiner", icon: Package, testid: "more-nav-deler", feature: "parts_page_enabled" },
];
const ADMIN_LINKS = [
  { to: "/brukarar", label: "Brukarar", desc: "Tilgang og roller", icon: UserCog, testid: "more-nav-brukarar" },
  { to: "/innstillingar", label: "Innstillingar", desc: "Firmaprofil, PDF og funksjonar", icon: Settings, testid: "more-nav-innstillingar" },
];

const Row = ({ icon: Icon, label, desc, onClick, testid, active, trailing, destructive }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-current={active ? "page" : undefined}
    className={cn(
      "pressable flex min-h-[56px] w-full items-center gap-3 rounded-[14px] px-2 text-left transition-colors duration-150",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
      active ? "bg-primary/10" : "hover:bg-accent",
      destructive && "text-destructive"
    )}
  >
    <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", active ? "bg-primary text-[#0B0D10]" : destructive ? "bg-destructive/10" : "bg-muted")}>
      <Icon className="h-5 w-5" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold">{label}</span>
      {desc && <span className="block truncate text-xs text-muted-foreground">{desc}</span>}
    </span>
    {trailing ?? <ChevronRight className="h-4 w-4 text-muted-foreground" />}
  </button>
);

/** Mobile «Meir»-drawer: secondary navigation, theme toggle, logout. */
export const MoreDrawer = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout, isAdmin, features } = useAuth();
  const { isDark, toggle } = useTheme();
  const taskSummary = useTaskSummary();
  const links = LINKS.filter((l) => !l.feature || features?.[l.feature]);
  const badgeFor = (l) => (l.badge === "tasks" && taskSummary?.open > 0
    ? <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums", taskSummary.overdue > 0 ? "bg-primary text-[#0B0D10]" : "bg-muted text-foreground")} data-testid={`${l.testid}-badge`}>{taskSummary.open}</span>
    : undefined);

  const go = (to) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="more-drawer">
        <DrawerHeader className="flex flex-row items-center gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#0B0D10] text-sm font-bold text-white dark:bg-white dark:text-[#0B0D10]">
            {initials(user?.name)}
          </span>
          <span className="min-w-0">
            <DrawerTitle className="truncate text-base" data-testid="more-user-name">{user?.name}</DrawerTitle>
            <DrawerDescription className="truncate text-xs">{user?.title || (user?.role === "admin" ? "Administrator" : "Mekaniker")} · {user?.email}</DrawerDescription>
          </span>
        </DrawerHeader>
        <DrawerBody className="space-y-1 pb-[calc(var(--safe-bottom)+20px)]">
          {links.map((l) => (
            <Row key={l.to} icon={l.icon} label={l.label} desc={l.desc} testid={l.testid} active={pathname.startsWith(l.to)} onClick={() => go(l.to)} trailing={badgeFor(l)} />
          ))}
          {isAdmin && (
            <>
              <div className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Administrasjon</div>
              {ADMIN_LINKS.map((l) => (
                <Row key={l.to} icon={l.icon} label={l.label} desc={l.desc} testid={l.testid} active={pathname.startsWith(l.to)} onClick={() => go(l.to)} />
              ))}
            </>
          )}
          <Separator className="my-2" />
          <Row icon={UserRound} label="Min konto" desc="Outlook-kopling og kontoopplysningar" testid="more-nav-profil" active={pathname === "/profil"} onClick={() => go("/profil")} />
          <Row
            icon={isDark ? Sun : Moon}
            label={isDark ? "Lys modus" : "Mørk modus"}
            desc="Byt fargetema"
            testid="more-theme-toggle"
            onClick={toggle}
            trailing={<Switch checked={isDark} onCheckedChange={toggle} aria-label="Mørk modus" data-testid="more-theme-switch" onClick={(e) => e.stopPropagation()} />}
          />
          <Row icon={LogOut} label="Logg ut" destructive testid="more-logout" onClick={() => { onOpenChange(false); logout(); navigate("/login"); }} trailing={<span />} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
